import { defineComponent as _defineComponent } from 'vue'
import { onMounted, onUnmounted, ref, watchEffect } from "vue";
import mp4Box from "mp4box";

import ActionBox from "./components/ActionBox.vue";
import MediaControlPanel from "./components/MediaControlPanel.vue";
import TopNavbar from "./components/TopNavbar.vue";
import useDewarp from "./service/useDewarp";
import {
  ACCEPT_TYPE,
  FILE_SIZE_UNITS,
  INITIAL_VIDEO_DURATION,
  VIDEO_DEVICE_SETTINGS,
} from "./constants";
import { getConvertedJSONFromBufferArray, readCrecBoxTimezone } from "./utils";
import type { CameraTimezoneDescriptor } from "./utils";
import { FileData } from "./types";

declare const config: {
  version?: string;
  buildNumber?: string;
  projectId?: string;
  showExportTimestamp?: boolean;
  showExportTimestampMilliseconds?: boolean;
};

interface FileBuffer extends ArrayBuffer {
  fileStart?: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const videoPlayer = ref<HTMLVideoElement>();
const videoPlayerContainer = ref<HTMLDivElement>();
const videoCanvas = ref<HTMLCanvasElement>();
const file = ref<FileData>();
const isVideoPaused = ref(true);
const isErrorVideo = ref(false);
const videoProcessingOnLoading = ref(false);
const mediaCurrentTime = ref(INITIAL_VIDEO_DURATION);
const videoDurationSeconds = ref(INITIAL_VIDEO_DURATION);
const shouldShowInfo = ref(false);
const isFishEyeVideo = ref(true);
const videoCurrentTimeUpdateInterval = ref();
const exportStartTimestampMs = ref<number | null>(null);
const shouldShowExportTimestamp = ref(false);
const cameraTimezone = ref<CameraTimezoneDescriptor>(null);

const infoDisplayTimeoutMs = 2000;
const videoCurrentTimeUpdateDelayInMS = 100;
const defaultMediaPlaybackRate = 1;
const uploadText = "Drag and drop exported video from Eagle Eye camera";
const uploadTextLine2 = "or";
const uploadButtonLabel = "Browse videos";
const wrongTypeErrorMessage = "Upload failed. Supported file types are video.";
const uploadFileErrorMessage = "Upload failed. Please try again.";
const infoCardText = "Drag to rotate, scroll to zoom.";
const portraitInfoText = "Please use landscape mode";
const errorButtonLabel = "Try again";
const errorTitle = "The video cannot be played";
const errorDescription =
  "The current video does not originate from an Eagle Eye camera";
const MP4_EPOCH_OFFSET_IN_SECONDS = 2082844800;
const shouldUseExportTimestamp =
  typeof config !== "undefined" && Boolean(config?.showExportTimestamp);
const showExportTimestampMilliseconds =
  typeof config !== "undefined" &&
  Boolean(config?.showExportTimestampMilliseconds);

const { dewarpVideo, setResize } = useDewarp();

const onWindowResize = () => {
  if (videoCanvas.value) {
    setResize({
      width: window.innerWidth,
      // Calculate height to keep aspect ratio of the canvas is 16/9
      height: (window.innerWidth / 16) * 9,
    });
  }
};

const onVideoLoadedMetadata = () => {
  videoDurationSeconds.value =
    videoPlayer.value?.duration ?? INITIAL_VIDEO_DURATION;
};

onMounted(() => window.addEventListener("resize", onWindowResize));

onUnmounted(() => window.removeEventListener("resize", onWindowResize));

watchEffect(() => {
  if (isVideoPaused.value) {
    clearInterval(videoCurrentTimeUpdateInterval.value);
    return;
  }

  videoCurrentTimeUpdateInterval.value = setInterval(() => {
    if (!videoPlayer.value) {
      return;
    }

    mediaCurrentTime.value = videoPlayer.value.currentTime;
  }, videoCurrentTimeUpdateDelayInMS);

});

const getTimestampFromKnownField = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const asNumber = Number(trimmedValue);
    if (Number.isFinite(asNumber)) {
      return getTimestampFromKnownField(asNumber);
    }

    const parsedDate = Date.parse(trimmedValue);
    if (!Number.isNaN(parsedDate)) {
      return parsedDate;
    }

    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    // Order matters: MP4_EPOCH_OFFSET_IN_SECONDS (2 082 844 800) is less
    // than 1e12, so a Unix-ms value would also pass the MP4-seconds check
    // and get mis-scaled. Test the largest magnitudes first.
    if (value > 1_000_000_000_000) {
      return value;
    }

    // MP4 creation_time values are in seconds since 1904-01-01.
    if (value > MP4_EPOCH_OFFSET_IN_SECONDS) {
      return (value - MP4_EPOCH_OFFSET_IN_SECONDS) * 1000;
    }

    if (value > 1_000_000_000) {
      return value * 1000;
    }
  }

  return null;
};

const extractTimestampFromMp4Metadata = (mp4BoxFile: any): number | null => {
  const headerCreationTimeCandidates = [
    mp4BoxFile?.moov?.mvhd?.creation_time,
    mp4BoxFile?.moov?.trak?.[0]?.tkhd?.creation_time,
    mp4BoxFile?.moov?.trak?.[0]?.mdia?.mdhd?.creation_time,
  ];

  for (const candidate of headerCreationTimeCandidates) {
    const parsed = getTimestampFromKnownField(candidate);
    if (parsed !== null) return parsed;
  }

  return null;
};

const processVideoFile = async (file: File) => {
  if (file) {
    const mp4BoxFile = mp4Box.createFile(false);

    const fileBuffer: FileBuffer = await file.arrayBuffer();
    fileBuffer.fileStart = 0;
    mp4BoxFile.onError = console.error;
    mp4BoxFile.appendBuffer(fileBuffer);
    mp4BoxFile.flush();

    const udtaData = mp4BoxFile.udta;

    const metadataTimestamp = extractTimestampFromMp4Metadata(mp4BoxFile);
    exportStartTimestampMs.value = metadataTimestamp;
    cameraTimezone.value = readCrecBoxTimezone(mp4BoxFile);
    shouldShowExportTimestamp.value =
      shouldUseExportTimestamp && metadataTimestamp !== null;

    videoPlayer.value?.setAttribute("src", URL.createObjectURL(file));
    videoProcessingOnLoading.value = false;
    if (!udtaData?.lens?.data || !udtaData?.dcon?.data) {
      isFishEyeVideo.value = false;

      return;
    }

    try {
      const convertedLens = getConvertedJSONFromBufferArray(udtaData.lens.data);
      const convertedDcon = getConvertedJSONFromBufferArray(udtaData.dcon.data);

      const updatedVideoDeviceSettings = {
        ...VIDEO_DEVICE_SETTINGS,
        fov: convertedLens.fov,
        mount: convertedDcon.mount,
      };

      if (
        videoCanvas.value &&
        videoPlayer.value &&
        videoPlayerContainer.value
      ) {
        dewarpVideo(
          updatedVideoDeviceSettings,
          videoCanvas.value,
          videoPlayerContainer.value,
          videoPlayer.value
        );
        onWindowResize();
      }
    } catch (error) {
      isErrorVideo.value = true;
    }
  }
};

const onUploadSuccess = (mediaData: FileData) => {
  videoProcessingOnLoading.value = true;
  exportStartTimestampMs.value = null;
  shouldShowExportTimestamp.value = false;
  cameraTimezone.value = null;
  videoDurationSeconds.value = INITIAL_VIDEO_DURATION;
  processVideoFile(mediaData.addedFile);
  file.value = mediaData;
};

const onVideoPlay = () => {
  if (videoPlayer.value?.paused) {
    videoPlayer.value.play();
    isVideoPaused.value = false;

    return;
  }

  if (videoPlayer.value?.play) {
    videoPlayer.value?.pause();
    isVideoPaused.value = true;
  }
};

const onUpdateCurrentTime = (
  time = INITIAL_VIDEO_DURATION,
  isIncrement = true
) => {
  if (videoPlayer.value) {
    const updatedTime = isIncrement
      ? videoPlayer.value.currentTime + time
      : time;
    videoPlayer.value.currentTime = updatedTime;
  }
};

const resetValues = () => {
  isVideoPaused.value = true;
  isErrorVideo.value = false;
  videoPlayer.value?.removeAttribute("src");
  file.value = undefined;
  isFishEyeVideo.value = true;
  mediaCurrentTime.value = INITIAL_VIDEO_DURATION;
  exportStartTimestampMs.value = null;
  shouldShowExportTimestamp.value = false;
  cameraTimezone.value = null;
  videoDurationSeconds.value = INITIAL_VIDEO_DURATION;
};

const handleVideoVolume = (volume: number) => {
  if (videoPlayer.value) {
    videoPlayer.value.volume = volume;
  }
};

const handleVideoSpeed = (speed: number) => {
  if (videoPlayer.value) {
    videoPlayer.value.playbackRate = speed;
  }
};

const onCanvasHover = () => {
  shouldShowInfo.value = true;

  setTimeout(() => {
    shouldShowInfo.value = false;
  }, infoDisplayTimeoutMs);
};

const onVideoEnd = () => {
  isVideoPaused.value = true;
  isErrorVideo.value = false;
  mediaCurrentTime.value = INITIAL_VIDEO_DURATION;
  if (videoPlayer.value) {
    videoPlayer.value.currentTime = INITIAL_VIDEO_DURATION;
  }
};

return { __sfc: true,videoPlayer, videoPlayerContainer, videoCanvas, file, isVideoPaused, isErrorVideo, videoProcessingOnLoading, mediaCurrentTime, videoDurationSeconds, shouldShowInfo, isFishEyeVideo, videoCurrentTimeUpdateInterval, exportStartTimestampMs, shouldShowExportTimestamp, cameraTimezone, infoDisplayTimeoutMs, videoCurrentTimeUpdateDelayInMS, defaultMediaPlaybackRate, uploadText, uploadTextLine2, uploadButtonLabel, wrongTypeErrorMessage, uploadFileErrorMessage, infoCardText, portraitInfoText, errorButtonLabel, errorTitle, errorDescription, MP4_EPOCH_OFFSET_IN_SECONDS, shouldUseExportTimestamp, showExportTimestampMilliseconds, dewarpVideo, setResize, onWindowResize, onVideoLoadedMetadata, getTimestampFromKnownField, extractTimestampFromMp4Metadata, processVideoFile, onUploadSuccess, onVideoPlay, onUpdateCurrentTime, resetValues, handleVideoVolume, handleVideoSpeed, onCanvasHover, onVideoEnd, ActionBox, MediaControlPanel, TopNavbar, ACCEPT_TYPE, FILE_SIZE_UNITS }
}

})